import { useState } from 'react';
import { ChevronDown, TrendingUp, TrendingDown, GitCompare, Calendar } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { eventsControllerCompareStats } from '@/generated/api/events/events';
import { EventsControllerCompareStatsComparisonType } from '@/generated/api/model';
import { useBrandsControllerGetBrands } from '@/generated/api/brands/brands';
import { PageHeader } from '@/shared/ui';
import { haptic } from '@/shared/utils/telegram';
import { formatInteger, formatCurrency } from '@/shared/utils/formatting';

type ComparisonType = EventsControllerCompareStatsComparisonType;

const COMPARISON_OPTIONS = [
  { value: EventsControllerCompareStatsComparisonType.day, label: 'Day', fullLabel: 'Today vs Yesterday' },
  { value: EventsControllerCompareStatsComparisonType.week, label: 'Week', fullLabel: 'This Week vs Last Week' },
  { value: EventsControllerCompareStatsComparisonType.month, label: 'Month', fullLabel: 'This Month vs Last Month' },
  { value: EventsControllerCompareStatsComparisonType.custom, label: 'Custom', fullLabel: 'Custom Dates' },
];

interface ComparisonData {
  comparisonType: string;
  current: {
    period: string;
    dateFrom: string;
    dateTo: string;
    summary: {
      totalClicks: number;
      totalRegs: number;
      totalFtds: number;
      totalCpas: number;
      totalDeps: number;
      totalRedeps: number;
      totalAmount: string;
      currency: string;
    };
  };
  previous: {
    period: string;
    dateFrom: string;
    dateTo: string;
    summary: {
      totalClicks: number;
      totalRegs: number;
      totalFtds: number;
      totalCpas: number;
      totalDeps: number;
      totalRedeps: number;
      totalAmount: string;
      currency: string;
    };
  };
  comparison: {
    clicks: { absolute: number; percentage: number };
    regs: { absolute: number; percentage: number };
    ftds: { absolute: number; percentage: number };
    cpas: { absolute: number; percentage: number };
    deps: { absolute: number; percentage: number };
    redeps: { absolute: number; percentage: number };
    amount: { absolute: number; percentage: number };
  };
  meta: {
    brand: string;
    generatedAt: string;
  };
}

export function ComparePage() {
  const [selectedBrand, setSelectedBrand] = useState<string>('__all__');
  const [comparisonType, setComparisonType] = useState<ComparisonType>(EventsControllerCompareStatsComparisonType.day);
  const [date1, setDate1] = useState<string>('');
  const [date2, setDate2] = useState<string>('');

  // Fetch brands list
  const { data: brandsData } = useBrandsControllerGetBrands();

  // Fetch comparison data
  const { data, isLoading, error } = useQuery<ComparisonData>({
    queryKey: ['comparison', selectedBrand, comparisonType, date1, date2],
    queryFn: async (): Promise<ComparisonData> => {
      const params: Record<string, string> = {
        comparisonType,
        excludeTest: 'true',
      };

      if (selectedBrand !== '__all__') {
        params.brand = selectedBrand;
      }

      if (comparisonType === EventsControllerCompareStatsComparisonType.custom) {
        if (!date1 || !date2) {
          throw new Error('Both dates are required for custom comparison');
        }
        params.date1 = date1;
        params.date2 = date2;
      }

      const response = await eventsControllerCompareStats(params as any);
      const data = typeof response === 'object' && response && 'data' in response ? response.data : response;
      return data as unknown as ComparisonData;
    },
    enabled: comparisonType !== EventsControllerCompareStatsComparisonType.custom || (!!date1 && !!date2),
    staleTime: 30000,
  });

  const handleBrandChange = (brand: string) => {
    haptic.impact('light');
    setSelectedBrand(brand);
  };

  const handleComparisonTypeChange = (type: ComparisonType) => {
    haptic.selection();
    setComparisonType(type);
  };

  // Helper to format comparison display with both current and previous values
  const ComparisonDisplay = ({
    current,
    previous,
    delta
  }: {
    current: number;
    previous: number;
    delta: { absolute: number; percentage: number };
  }) => {
    const isPositive = delta.absolute > 0;
    const isNeutral = delta.absolute === 0;
    const color = isNeutral ? 'text-[var(--tg-theme-hint-color)]' : isPositive ? 'text-emerald-500' : 'text-rose-500';
    const Icon = isNeutral ? null : isPositive ? TrendingUp : TrendingDown;

    // Format percentage display
    let percentageText = '';
    if (previous === 0 && current > 0) {
      // New data appeared
      percentageText = 'new';
    } else if (previous === 0 && current === 0) {
      // No data in both periods
      percentageText = '—';
    } else {
      // Normal percentage calculation
      percentageText = `${isPositive && delta.percentage !== 0 ? '+' : ''}${delta.percentage.toFixed(1)}%`;
    }

    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-black tabular-nums">{formatInteger(current)}</span>
          <span className="text-xs text-[var(--tg-theme-hint-color)] font-medium">
            vs {formatInteger(previous)}
          </span>
        </div>
        <div className={`flex items-center gap-1.5 ${color}`}>
          <span className="text-sm font-black">
            {isPositive && '+'}{delta.absolute} ({percentageText})
          </span>
          {Icon && <Icon size={14} />}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 duration-500 pb-20">
      {/* Header - Sticky */}
      <div className="flex flex-col gap-3 sticky top-0 bg-[var(--tg-theme-bg-color)] z-20 pt-2 pb-1">
        <PageHeader title="Compare" />

        {/* Brand Selector */}
        {brandsData?.data?.brands && brandsData.data.brands.length > 0 && (
          <div className="relative">
            <select
              value={selectedBrand}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="w-full h-11 px-4 pr-10 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
            >
              <option value="__all__">All Brands</option>
              {brandsData.data.brands.map((brand) => (
                <option key={brand.brand} value={brand.brand}>
                  {brand.brand} ({brand.totalEvents} events)
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <ChevronDown size={16} className="text-[var(--tg-theme-hint-color)]" />
            </div>
          </div>
        )}

        {/* Comparison Type Selector */}
        <div className="flex p-1 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl overflow-x-auto scrollbar-hide">
          {COMPARISON_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => handleComparisonTypeChange(option.value)}
              title={option.fullLabel}
              className={`flex-1 py-2.5 text-sm font-black rounded-lg transition-all min-w-[60px] ${
                comparisonType === option.value
                  ? 'bg-[var(--tg-theme-bg-color)] shadow-sm text-[var(--tg-theme-link-color)]'
                  : 'text-[var(--tg-theme-hint-color)] opacity-60'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Custom Date Pickers */}
        {comparisonType === EventsControllerCompareStatsComparisonType.custom && (
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <input
                type="date"
                value={date1}
                onChange={(e) => setDate1(e.target.value)}
                className="w-full h-11 px-4 pr-10 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
                placeholder="Date 1"
              />
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <Calendar size={16} className="text-[var(--tg-theme-hint-color)]" />
              </div>
            </div>
            <div className="relative">
              <input
                type="date"
                value={date2}
                onChange={(e) => setDate2(e.target.value)}
                className="w-full h-11 px-4 pr-10 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
                placeholder="Date 2"
              />
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <Calendar size={16} className="text-[var(--tg-theme-hint-color)]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-5 animate-in fade-in slide-in-from-right duration-300">
        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--tg-theme-link-color)] border-t-transparent" />
              <p className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase">Loading comparison...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-6 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-center">
            <p className="text-sm font-bold text-rose-600">Failed to load comparison</p>
            <p className="text-xs text-rose-500/80 mt-1">
              {comparisonType === EventsControllerCompareStatsComparisonType.custom
                ? 'Please select both dates'
                : 'Please try again later'}
            </p>
          </div>
        )}

        {/* Comparison Results */}
        {data && (
          <>
            {/* Period Labels */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20">
                <div className="text-[10px] font-black text-blue-600 uppercase tracking-wider mb-1">Current</div>
                <div className="text-xl font-black text-blue-600">{data.current.period}</div>
              </div>
              <div className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] border border-black/5 dark:border-white/5">
                <div className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider mb-1">Previous</div>
                <div className="text-xl font-black text-[var(--tg-theme-hint-color)]">{data.previous.period}</div>
              </div>
            </div>

            {/* Metrics Comparison Cards */}
            <div className="grid grid-cols-2 gap-3">
              {/* Clicks */}
              <div className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] flex flex-col gap-1 border border-black/5 dark:border-white/5">
                <span className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">Clicks</span>
                <ComparisonDisplay
                  current={data.current.summary.totalClicks}
                  previous={data.previous.summary.totalClicks}
                  delta={data.comparison.clicks}
                />
              </div>

              {/* REG */}
              <div className="p-4 rounded-2xl bg-blue-500/10 flex flex-col gap-1 border border-blue-500/20">
                <span className="text-[10px] font-black text-blue-600 uppercase tracking-wider">REG</span>
                <ComparisonDisplay
                  current={data.current.summary.totalRegs}
                  previous={data.previous.summary.totalRegs}
                  delta={data.comparison.regs}
                />
              </div>

              {/* CPA - PRIMARY */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 flex flex-col gap-1.5 border-2 border-amber-500/30">
                <span className="text-[10px] font-black text-amber-600 uppercase tracking-wider">CPA</span>
                <ComparisonDisplay
                  current={data.current.summary.totalCpas}
                  previous={data.previous.summary.totalCpas}
                  delta={data.comparison.cpas}
                />
              </div>

              {/* FTD */}
              <div className="p-4 rounded-2xl bg-emerald-500/10 flex flex-col gap-1 border border-emerald-500/20">
                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-wider">FTD</span>
                <ComparisonDisplay
                  current={data.current.summary.totalFtds}
                  previous={data.previous.summary.totalFtds}
                  delta={data.comparison.ftds}
                />
              </div>

              {/* REDEP */}
              <div className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] flex flex-col gap-1 border border-black/5 dark:border-white/5">
                <span className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">REDEP</span>
                <ComparisonDisplay
                  current={data.current.summary.totalRedeps}
                  previous={data.previous.summary.totalRedeps}
                  delta={data.comparison.redeps}
                />
              </div>

              {/* DEP */}
              <div className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] flex flex-col gap-1 border border-black/5 dark:border-white/5">
                <span className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">DEP</span>
                <ComparisonDisplay
                  current={data.current.summary.totalDeps}
                  previous={data.previous.summary.totalDeps}
                  delta={data.comparison.deps}
                />
              </div>
            </div>

            {/* CPA Revenue Comparison */}
            {(parseFloat(data.current.summary.totalAmount || '0') > 0 || parseFloat(data.previous.summary.totalAmount || '0') > 0) && (() => {
              const currentAmount = parseFloat(data.current.summary.totalAmount || '0');
              const previousAmount = parseFloat(data.previous.summary.totalAmount || '0');
              const isPositive = data.comparison.amount.absolute > 0;
              const isNeutral = data.comparison.amount.absolute === 0;

              // Format percentage display for revenue
              let revenuePercentageText = '';
              if (previousAmount === 0 && currentAmount > 0) {
                revenuePercentageText = 'new';
              } else if (previousAmount === 0 && currentAmount === 0) {
                revenuePercentageText = '—';
              } else {
                revenuePercentageText = `${isPositive && data.comparison.amount.percentage !== 0 ? '+' : ''}${data.comparison.amount.percentage.toFixed(1)}%`;
              }

              return (
                <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-transparent p-4 rounded-3xl border border-amber-500/20">
                  <div className="text-xs font-black text-amber-600 uppercase tracking-widest mb-1.5">CPA Revenue</div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-baseline gap-2">
                      <div className="text-3xl font-black text-amber-600 tabular-nums">
                        {formatCurrency(currentAmount, data.current.summary.currency)}
                      </div>
                      <span className="text-xs text-[var(--tg-theme-hint-color)] font-medium">
                        vs {formatCurrency(previousAmount, data.previous.summary.currency)}
                      </span>
                    </div>
                    <div className={`flex items-center gap-1.5 ${
                      isNeutral ? 'text-[var(--tg-theme-hint-color)]' : isPositive ? 'text-emerald-500' : 'text-rose-500'
                    }`}>
                      <span className="text-sm font-black">
                        {isPositive && '+'}
                        {formatCurrency(Math.abs(data.comparison.amount.absolute), data.current.summary.currency)}
                        ({revenuePercentageText})
                      </span>
                      {isPositive && <TrendingUp size={14} />}
                      {!isPositive && !isNeutral && <TrendingDown size={14} />}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Summary Card */}
            <div className="bg-[var(--tg-theme-secondary-bg-color)] p-4 rounded-3xl border border-black/5 dark:border-white/5">
              <div className="flex items-center gap-2 mb-3">
                <GitCompare size={16} className="text-[var(--tg-theme-link-color)]" />
                <h3 className="text-xs font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest">Summary</h3>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-[var(--tg-theme-hint-color)]">Brand:</span>
                  <span className="font-bold">{data.meta.brand === 'all_brands' ? 'All Brands' : data.meta.brand}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--tg-theme-hint-color)]">Type:</span>
                  <span className="font-bold capitalize">{data.comparisonType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--tg-theme-hint-color)]">Generated:</span>
                  <span className="font-bold">
                    {new Date(data.meta.generatedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
